/**
 * Registers every `executionMode: 'client-side'` entity definition from
 * `@fias/client-entities` against the client-entity registry. Side-effect
 * imported once from `main.tsx` so registration happens before any arche
 * renders.
 *
 * This module is the ONLY intended consumer of the definition objects — it
 * is the seam between the executor implementations (the workspace package)
 * and the registry (the single client-lane dispatch point, which owns
 * permission gating and rate limiting). Registration is idempotent:
 * `registerClientEntity` replaces a prior registration for the same ID, so
 * re-evaluating this module (Vite HMR, `vi.resetModules()`) is safe.
 *
 * Add a new client-side entity: export its definition from
 * `@fias/client-entities` and register it here. The architecture test
 * `tests/architecture/client-entity-registry-mirrors-entity-config.test.ts`
 * pins the definition literals against the entity catalog AND asserts this
 * module registers every definition — a forgotten registration fails CI.
 */

import { registerClientEntity } from '@/lib/client-entity-registry';
import {
  nllbTranslationDefinition,
  grammarCheckDefinition,
  pluginContentDefinition,
  configurePluginContentHost,
  connectionsConnectDefinition,
  configureConnectionsConnectHost,
  actionConfirmationDefinition,
  configureActionConfirmationHost,
  appAccessDialogDefinition,
  configureAppAccessDialogHost,
  archeAssetBytesDefinition,
  configureArcheAssetBytesHost,
  pluginLocalStoreDefinition,
  configurePluginLocalStoreHost,
} from '@fias/client-entities';
import { pluginContentHost } from '@/lib/plugin-content-host';
import { connectionsConnectHost } from '@/lib/connections-connect-host';
import { actionConfirmationHost } from '@/lib/action-confirmation-host';
import { appAccessDialogHost } from '@/lib/app-access-dialog-host';
import { API_ORIGIN } from '@/lib/api-base';
import { captureError } from '@/lib/telemetry';

registerClientEntity(nllbTranslationDefinition.entityId, nllbTranslationDefinition);
registerClientEntity(grammarCheckDefinition.entityId, grammarCheckDefinition);
// Plugin content packs read through this page's credential + cache, which the
// executor receives here because its context carries only { userId, archeId }.
configurePluginContentHost(pluginContentHost);
registerClientEntity(pluginContentDefinition.entityId, pluginContentDefinition);
// Connecting an outside account draws the HOST's sheet for the mounted arche
// (ADR-029 §2); the executor reaches it through this adapter.
configureConnectionsConnectHost(connectionsConnectHost);
registerClientEntity(connectionsConnectDefinition.entityId, connectionsConnectDefinition);
// Approving an app's outward action draws the HOST's confirm sheet for the
// mounted arche (ADR-029 §4); the executor reaches it through this adapter.
configureActionConfirmationHost(actionConfirmationHost);
registerClientEntity(actionConfirmationDefinition.entityId, actionConfirmationDefinition);
// The person's access to an app's workspace data — "Manage access" / "Request
// access" (ADR-034) — drawn by the HOST for the mounted arche in the acting
// workspace; the executor reaches it through this adapter.
configureAppAccessDialogHost(appAccessDialogHost);
registerClientEntity(appAccessDialogDefinition.entityId, appAccessDialogDefinition);

// Asset-library byte reads fetch a signed URL the plugin already obtained, from
// the API origin those URLs are rooted at.
configureArcheAssetBytesHost({ apiOrigin: API_ORIGIN });
registerClientEntity(archeAssetBytesDefinition.entityId, archeAssetBytesDefinition);

// ADR-031: plugins' on-device working data, kept in this page's IndexedDB. The
// database name carries the environment so a local build never reads a
// staging journal in the same browser. The sign-out clear and the sign-in
// sweep run from AuthContext, not from here; they never throw (signing in or
// out must not fail because of device storage), so their failures reach
// telemetry through `reportError` instead.
configurePluginLocalStoreHost({
  environment: (import.meta.env.VITE_ENVIRONMENT as string | undefined) || 'development',
  indexedDB: typeof indexedDB === 'undefined' ? undefined : indexedDB,
  storage: typeof navigator === 'undefined' ? undefined : navigator.storage,
  reportError: (operation, error) =>
    captureError(error instanceof Error ? error : new Error(String(error)), {
      source: 'plugin-local-store',
      operation,
    }),
});
registerClientEntity(pluginLocalStoreDefinition.entityId, pluginLocalStoreDefinition);
